import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Image, Form, Button } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import Card from "../../../components/Card";
import axios from "axios";
import { Formik } from "formik";
import logo from "../../../assets/images/coyote-logo.jpg";
import * as Yup from "yup";
import url from "../../../configs/config";
import auth2 from "../../../assets/images/auth/02.png";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import FocusError from "../../../components/error/error-focus";
import InputField from "../../../components/input/input-field";

const ForgotPassword = () => {
  const location = useLocation();
  const alertRef = useRef(null);
  const [initialEmail, setInitialEmail] = useState("");
  const [errorEmail, setErrorEmail] = useState([]);
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  useEffect(() => {
    if (location?.state) {
      setInitialEmail(location.state);
    }
  }, [location.state]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      const response = await axios.put(
        `${url.backendURL}user/forgot-password`,
        {
          email: values.email,
        }
      );
      setErrorEmail([]);
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setAlert({
        message: response && response.data.message,
        type: "success",
        show: true,
        alertFor: "forgot",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } catch (err) {
      const { response } = err;
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setErrorEmail(response.data.error?.email);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor: "forgot",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = () =>
    Yup.object().shape({
      email: Yup.string()
        .trim()
        .required("Email is required")
        .email("Enter a valid email address")
        .test(
          "no-spaces",
          "Email may not contain any spaces at the beginning or end",
          (value) => !value || value.trim() === value
        )
        .test("min-domain-segments", "Enter a valid email address", (value) => {
          if (!value) return true;
          const parts = value.split("@");
          if (parts.length !== 2) return false;
          const domainSegments = parts[1].split(".");
          return (
            domainSegments.length >= 2 &&
            domainSegments.every((s) => s.length >= 2)
          );
        })
        .test("realistic-email", "Enter a valid email address", (value) => {
          if (!value) return true;
          const [local, domain] = value.split("@");
          if (!local || !domain) return false;

          if (/^\d+$/.test(local)) return false;
          if (local.length > 64) return false;

          if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain)) return false;

          return true;
        }),
    });

  return (
    <>
      <section className="login-content">
        <Row className="m-0 align-items-center bg-white vh-100">
          <Col
            md="6"
            className="d-md-block d-none bg-primary p-0 mt-n1 vh-100 overflow-hidden"
          >
            <Image
              src={auth2}
              className="img-fluid gradient-main animated-scaleX"
              alt="images"
            />
          </Col>
          <Col md="6" className="p-0" ref={alertRef}>
            <Card className="card-transparent auth-card shadow-none d-flex justify-content-center mb-0">
              <Card.Body>
                <AlertNotificationSimple
                  type={alert.type}
                  message={alert.message}
                  show={alert.show}
                  onClose={() => setAlert({ ...alert, show: false })}
                />
                <Link
                  to="/user/auth/sign-in"
                  className="navbar-brand d-flex align-items-center mb-3"
                >
                  <img
                    className="logo-title text-center mb-2 coyote-logo-image h-auto w-25"
                    src={logo}
                    alt="coyote-logo"
                  />
                </Link>
                <h2 className="mb-2">Reset Password</h2>
                <p>
                  Enter your email address and we&apos;ll send you an email with
                  instructions to reset your password.
                </p>
                <Formik
                  initialValues={{ email: initialEmail }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    values,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    handleSubmit,
                    isSubmitting,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <FocusError />
                      <Row>
                        <Col lg="12" className="col-lg-12">
                          <InputField
                            className="floating-label"
                            type="email"
                            name="email"
                            placeholder="example@email.com"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            asterick={true}
                            errorMessage={errorEmail}
                            label="Email"
                          />
                        </Col>
                      </Row>
                      <Button
                        className="mt-3"
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting}
                      >
                        Reset
                      </Button>
                    </Form>
                  )}
                </Formik>
              </Card.Body>
            </Card>
            <div className="sign-bg sign-bg-right">
              <svg
                width="280"
                height="230"
                viewBox="0 0 431 398"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g opacity="0.05">
                  <rect
                    x="-157.085"
                    y="193.773"
                    width="543"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(-45 -157.085 193.773)"
                    fill="#3B8AFF"
                  />
                  <rect
                    x="7.46875"
                    y="358.327"
                    width="543"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(-45 7.46875 358.327)"
                    fill="#3B8AFF"
                  />
                  <rect
                    x="61.9355"
                    y="138.545"
                    width="310.286"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(45 61.9355 138.545)"
                    fill="#3B8AFF"
                  />
                  <rect
                    x="62.3154"
                    y="-190.173"
                    width="543"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(45 62.3154 -190.173)"
                    fill="#3B8AFF"
                  />
                </g>
              </svg>
            </div>
          </Col>
        </Row>
      </section>
    </>
  );
};

export default ForgotPassword;
